<!-- eslint-disable max-len -->
<template>
  <div
    :class="{ 'is-loading': isLoading }"
    class="bag-gallery">
    <div
      v-if="frameSpec?.previewImage?.url && !isLoading"
      class="bag-gallery__uploaded bag-gallery__uploaded-image"
    >
      <ResponsiveImage
        :image="frameSpec.previewImage.url"
        :image-alt="title"
        :inlineIr="(true)"
      />
    </div>
    <div
      v-else-if="bundleSpecNumber && bundleSpecToken && !isLoading"
      class="bag-gallery__bundle"
    >
      <BundleThumbnailSVGVanilla
        :id="lineItemId"
        :bundle-spec-number="bundleSpecNumber"
        :token="bundleSpecToken"
        :joinery-host="joineryHost"
        :croppedAndNormalizedUrl="croppedAndNormalizedUrl"
      />
    </div>
    <ResponsiveImage
      v-else-if="image && !isLoading"
      :image="image"
      :image-alt="title"
      :image-class="'bag-item__photo-img'"
      :inlineIr="(true)"
      :wrapperClass="'bag-item__photo-wrapper'"
    />
    <button
      v-if="!disableGallery"
      class="bag-gallery__zoom-btn btn-icon"
      @click.prevent="modalOpen = true"
    >
      <span class="screenreader">Open Photo Gallery</span>
      <span class="icon icon--zoom-out-half"></span>
    </button>
    <VueModal
      v-if="!disableGallery"
      :show="(modalOpen)"
      :includeOverlay="(false)"
      :isFullscreen="(true)"
      :afterOpen="afterOpen"
      @toggleModal="toggleModal"
    >
      <template #modal-content>
        <div v-if="product" class="vue-modal__body--fullscreen">
          <ProductGalleryZoomCart
            :product="product"
            :currentVariant="currentVariant"
            :shouldIncludeFirstSlide="shouldIncludeFirstSlide"
          />
        </div>
        <div v-else>Loading...</div>
      </template>
    </VueModal>
    <div v-if="shouldIncludeFirstSlide">
      <Teleport to="#pvGalleryFirstSlide">
        <!-- Insert FrameSpec Preview Image -->
        <div
          v-if="frameSpec?.previewImage?.url"
          class="pv-gallery__item-upload pv-gallery__item-image"
        >
          <ResponsiveImage
            :image="frameSpec.previewImage.url"
            :image-alt="title"
            :inlineIr="(true)"
            :wrapperClass="'pv-gallery__item-upload-image'"
          />
        </div>

        <!-- Insert BundleSpec -->
        <div
          v-else-if="bundleSpecNumber && bundleSpecToken"
          class="pv-gallery__item-upload"
        >
          <BundleThumbnailSVGVanilla
            :id="lineItemId"
            :frameSpec="frameSpec"
            :bundle-spec-number="bundleSpecNumber"
            :token="bundleSpecToken"
            :joinery-host="joineryHost"
            :croppedAndNormalizedUrl="croppedAndNormalizedUrl"
          />
        </div>
      </Teleport>
    </div>
  </div>
</template>
<script>
import {
  ref, onMounted, watch, computed,
} from 'vue';
import BundleThumbnailSVGVanilla from '@framebridge/toolbox/BundleThumbnailSVGVanilla';
import { mafMatOptions } from '@framebridge/toolbox/hardCodedValues';
import ResponsiveImage from '../common/responsive-image/ResponsiveImage';
import VueModal from '../common/modal/Modal';
import ProductGalleryZoomCart from './ProductGalleryZoomCart.vue';
import productGalleryZoomCarousel from '../../lib/product-gallery-zoom-carousel';

export default {
  name: 'CartItemGallery',
  components: {
    ResponsiveImage,
    BundleThumbnailSVGVanilla,
    VueModal,
    ProductGalleryZoomCart,
  },
  props: {
    image: {
      default: null,
      required: true,
      type: String,
    },
    title: {
      default: '',
      required: true,
      type: String,
    },
    url: {
      type: String,
      required: true,
    },
    frameSpec: {
      default: null,
      type: Object,
    },
    bundleSpecNumber: {
      type: String,
    },
    bundleSpecToken: {
      type: String,
    },
    isLoading: {
      default: false,
      type: Boolean,
    },
    disableGallery: {
      default: false,
      type: Boolean,
    },
    lineItemId: {
      type: String,
      required: false,
    },
    variantId: {
      type: String,
      required: true,
    },
    productHandle: {
      type: String,
      required: true,
    }
  },

  setup(props) {
    const modalOpen = ref(false);
    const croppedAndNormalizedUrl = ref(null);
    const joineryHost = ref(SDG.Data.joineryHost);
    const product = ref(null);
    const variants = ref([]);
    const currentVariant = ref(null);

    const toggleModal = (value) => {
      modalOpen.value = value;
    };

    const afterOpen = () => {
      productGalleryZoomCarousel({
        dom: {
          carousel: '#vueModalRef .js-product-gallery-zoom-carousel',
          videoBtn: '.js-video-control',
        },
        arrangeImages: false,
      });
      SDG.videoHTML();
    };

    const setCroppedUrl = (croppedUrl) => {
      croppedAndNormalizedUrl.value = croppedUrl;
    };

    const fetchProductData = async () => {
      try {
        const response = await fetch(`${props.url}&view=json`);
        if (!response.ok) throw new Error('Failed to fetch product data');
        const data = await response.json();

        // Set product and variants
        product.value = data.product.productJson;

        // Merge top-level variant metafields into product.variants
        const variantMetafields = data.product.variants.metafields || {};
        product.value.variants = product.value.variants.map((variant) => {
          const metafields = variantMetafields[variant.id] || {};
          return {
            ...variant,
            metafields,
          };
        });

        // Identify the current variant and ensure it includes metafields
        currentVariant.value = product.value.variants.find(
          (variant) => variant.id === Number(props.variantId)
        );

        if (!currentVariant.value) {
          throw new Error('Current variant not found');
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    };

    onMounted(() => {
      if (props.disableGallery) return;
      fetchProductData();
    });

    const isCustomProduct = computed(() => {
      const isBundle = !!props.bundleSpecNumber && !!props.bundleSpecToken;
      const isCustomFrame = !!props.frameSpec;
      return isBundle || isCustomFrame;
    });

    const shouldIncludeFirstSlide = computed(() => {
      return isCustomProduct.value && !props.isLoading && modalOpen.value;
    });

    watch(() => props.frameSpec, () => {
      if (props.frameSpec !== null) {
        setCroppedUrl(props.frameSpec?.artworks[0]?.croppedAndNormalized.url);
      }
    });

    const frameSpecWithMafMat = computed(() => {
      const fs = props?.frameSpec?.clone();
      if (fs?.hasMat() && mafMatOptions.includes(fs.mats[0].name)) {
        fs.getArtwork().completedMafMatName = true;
      }
      return fs;
    });

    return {
      modalOpen,
      toggleModal,
      afterOpen,
      frameSpecWithMafMat,
      isCustomProduct,
      shouldIncludeFirstSlide,
      joineryHost,
      product,
      variants,
      currentVariant,
    };
  },
};
</script>
